import { useCallback, useEffect, useState } from "react";
import { Menu, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Loader } from "@/components/ui/Loader";
import { cn } from "@/utils/cn";
import { useAuth } from "@/hooks/useAuth";
import { useTarefasAutoRefresh } from "@/hooks/useTarefasAutoRefresh";
import { QuadroSemanalTarefas } from "./QuadroSemanalTarefas";
import { TarefasSidebar, type TarefasNavView } from "./TarefasSidebar";
import { TarefasLegenda } from "./TarefasLegenda";
import { ModalNovaTarefa } from "./ModalNovaTarefa";
import { ModalExcluirRecorrente } from "./ModalExcluirRecorrente";
import { ModalNovoGrupo } from "./ModalNovoGrupo";
import { TarefaTimelineModal } from "./TarefaTimelineModal";
import { getWeekStartFromKey, todayKey } from "./dateUtils";
import { getOcorrenciaData, isTarefaRecorrente } from "./recorrenciaUtils";
import {
  createTarefa,
  createTarefaGrupo,
  deleteTarefa,
  deleteTarefaGrupo,
  fetchTarefaGrupos,
  listTarefas,
  updateTarefa,
  type TarefaDeleteEscopo,
  type TarefaDto,
  type TarefaFormValues,
  type TarefaGrupoDto,
  type TarefaStatus,
  type TarefaTipo,
} from "@/services/tarefas";

export function TarefasPage() {
  const { user } = useAuth();
  const userName = user?.displayName ?? "Você";

  const [weekStart, setWeekStart] = useState(() => getWeekStartFromKey(todayKey()));
  const [tarefas, setTarefas] = useState<TarefaDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<TarefaStatus | "">("");
  const [grupoFilter, setGrupoFilter] = useState("");
  const [grupos, setGrupos] = useState<TarefaGrupoDto[]>([]);
  const [navView, setNavView] = useState<TarefasNavView>("todas");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState(todayKey());
  const [modalDefaultTipo, setModalDefaultTipo] = useState<TarefaTipo>("tarefa");
  const [grupoModalOpen, setGrupoModalOpen] = useState(false);
  const [editing, setEditing] = useState<TarefaDto | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [timelineTarefa, setTimelineTarefa] = useState<TarefaDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TarefaDto | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await listTarefas();
      setTarefas(data);
    } catch {
      if (!opts?.silent) {
        setError("Não foi possível carregar as tarefas.");
      }
    } finally {
      if (!opts?.silent) {
        setLoading(false);
      }
    }
  }, []);

  useTarefasAutoRefresh(load);

  useEffect(() => {
    void load();
    fetchTarefaGrupos().then(setGrupos).catch(() => setGrupos([]));
  }, [load]);

  const openNovaTarefa = (data: string) => {
    setEditing(null);
    setModalDate(data);
    setModalDefaultTipo(navView === "calendario" ? "evento" : "tarefa");
    setModalOpen(true);
  };

  const openNovaFromSidebar = () => openNovaTarefa(todayKey());

  const handleCreateGrupo = async (nome: string) => {
    const group = await createTarefaGrupo(nome, "tipo");
    setGrupos((prev) => [...prev, group].sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")));
  };

  const handleDeleteGrupo = async (grupo: TarefaGrupoDto) => {
    if (!window.confirm(`Excluir o grupo "${grupo.nome}"? As tarefas permanecem, mas ficam sem grupo.`)) return;
    try {
      await deleteTarefaGrupo(grupo.id);
      setGrupos((prev) => prev.filter((g) => g.id !== grupo.id));
      if (grupoFilter === grupo.id) setGrupoFilter("");
      await load();
    } catch {
      setError("Não foi possível excluir o grupo.");
    }
  };

  const openEdit = (tarefa: TarefaDto) => {
    setEditing(tarefa);
    setModalDate(tarefa.data);
    setModalDefaultTipo(tarefa.tipo ?? "tarefa");
    setModalOpen(true);
  };

  const handleSave = async (values: TarefaFormValues, editingId: string | null) => {
    const savedTipo = values.tipo;
    let saved: TarefaDto;

    if (editingId) {
      saved = await updateTarefa(editingId, values);
      setTarefas((prev) => prev.map((t) => (t.id === editingId ? saved : t)));
    } else {
      const created = await createTarefa(values);
      saved = created.tipo ? created : { ...created, tipo: savedTipo };
      setTarefas((prev) => [...prev, saved]);
    }

    setWeekStart(getWeekStartFromKey(values.data));

    const isEvento = savedTipo === "evento" || saved.tipo === "evento";
    if (isEvento) {
      setNavView("calendario");
    } else if (navView === "calendario") {
      setNavView("todas");
    }

    await load();
  };

  const requestDelete = (tarefa: TarefaDto) => {
    if (isTarefaRecorrente(tarefa)) {
      setDeleteTarget(tarefa);
      return;
    }
    if (!window.confirm(`Excluir "${tarefa.titulo}"?`)) return;
    void executeDelete(tarefa, "todas");
  };

  const executeDelete = async (tarefa: TarefaDto, escopo: TarefaDeleteEscopo) => {
    setBusyId(tarefa.id);
    try {
      await deleteTarefa(tarefa.id, {
        escopo: isTarefaRecorrente(tarefa) ? escopo : undefined,
        ocorrenciaData: getOcorrenciaData(tarefa),
      });
      setDeleteTarget(null);
      setModalOpen(false);
      setEditing(null);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleStatusChange = async (tarefa: TarefaDto, status: TarefaStatus) => {
    setBusyId(tarefa.id);
    try {
      await updateTarefa(tarefa.id, { status });
      await load();
      if (editing?.id === tarefa.id) {
        const fresh = (await listTarefas()).find((t) => t.id === tarefa.id);
        if (fresh) setEditing(fresh);
      }
      if (timelineTarefa?.id === tarefa.id) {
        const fresh = (await listTarefas()).find((t) => t.id === tarefa.id);
        if (fresh) setTimelineTarefa(fresh);
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleMoveTarefa = async (tarefa: TarefaDto, newDate: string) => {
    if (tarefa.data === newDate) return;
    setBusyId(tarefa.id);
    try {
      await updateTarefa(tarefa.id, { data: newDate });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleNavViewChange = (view: TarefasNavView) => {
    setNavView(view);
    if (view === "hoje" || view === "calendario") {
      setWeekStart(getWeekStartFromKey(todayKey()));
    }
  };

  const pageSubtitle =
    navView === "calendario"
      ? "Calendário de eventos — semana completa"
      : "Quadro semanal de tarefas";

  return (
    <div className="absolute inset-0 flex min-h-0 flex-col overflow-hidden">
      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3 sm:px-5">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="lg:hidden"
            leftIcon={<Menu className="h-4 w-4" />}
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menu de tarefas"
          >
            Menu
          </Button>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-bold text-slate-800">Tarefas</h1>
            <p className="truncate text-xs text-slate-500">{pageSubtitle}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          leftIcon={<RefreshCw className="h-4 w-4" />}
          onClick={() => void load()}
          loading={loading}
        >
          <span className="hidden sm:inline">Atualizar</span>
        </Button>
      </div>

      {error && (
        <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 overflow-hidden bg-slate-100/60">
        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
            aria-label="Fechar menu"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <TarefasSidebar
          statusFilter={statusFilter}
          grupoFilter={grupoFilter}
          navView={navView}
          userName={userName}
          grupos={grupos}
          onNovaTarefa={() => {
            openNovaFromSidebar();
            setSidebarOpen(false);
          }}
          onNovoGrupo={() => setGrupoModalOpen(true)}
          onExcluirGrupo={(g) => void handleDeleteGrupo(g)}
          onStatusFilterChange={setStatusFilter}
          onGrupoFilterChange={setGrupoFilter}
          onNavViewChange={handleNavViewChange}
          onClose={() => setSidebarOpen(false)}
          className={cn(
            "fixed inset-y-0 left-0 z-50 shadow-xl transition-transform lg:static lg:z-auto lg:shadow-none",
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          )}
        />

        {loading && tarefas.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader size="lg" label="Carregando tarefas..." />
          </div>
        ) : (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <QuadroSemanalTarefas
              weekStart={weekStart}
              tarefas={tarefas}
              statusFilter={statusFilter}
              grupoFilter={grupoFilter}
              navView={navView}
              currentUserId={user?.id ?? ""}
              busyId={busyId}
              onWeekChange={setWeekStart}
              onNovaTarefa={openNovaTarefa}
              onEdit={openEdit}
              onLeadtime={setTimelineTarefa}
              onStatusChange={handleStatusChange}
              onMoveToDay={handleMoveTarefa}
            />
            <TarefasLegenda />
          </div>
        )}
      </div>

      <ModalNovaTarefa
        open={modalOpen}
        data={modalDate}
        editing={editing}
        defaultTipo={modalDefaultTipo}
        grupos={grupos}
        busy={busyId === editing?.id}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
        onDelete={
          editing && editing.criadorId === user?.id
            ? () => requestDelete(editing)
            : undefined
        }
      />

      <ModalExcluirRecorrente
        open={Boolean(deleteTarget)}
        tarefa={deleteTarget}
        busy={busyId === deleteTarget?.id}
        onClose={() => setDeleteTarget(null)}
        onConfirm={(escopo) => {
          if (deleteTarget) void executeDelete(deleteTarget, escopo);
        }}
      />

      <ModalNovoGrupo
        open={grupoModalOpen}
        onClose={() => setGrupoModalOpen(false)}
        onSave={handleCreateGrupo}
      />

      {timelineTarefa && (
        <TarefaTimelineModal
          open
          onClose={() => setTimelineTarefa(null)}
          tarefa={timelineTarefa}
        />
      )}
    </div>
  );
}
